import { action, computed, flow, makeObservable, observable, reaction } from 'mobx';
import { computedFn } from 'mobx-utils';

import { ViewSettings } from 'src/api/chart/drilling-plan-charts-api';
import type { RigsChartViewTypes } from 'src/api/chart/rigs-chart-api';
import i18n from 'src/i18n';
import type { TPaletteSettingsData } from 'src/pages/settings-page/features/palette-settings/palette-settings.store';
import { Controls } from 'src/shared/constants/controls';
import type { PadAttributeIcon } from 'src/shared/constants/pad-attribute-icon';
import { isPadAttributeIcon } from 'src/shared/constants/pad-attribute-icon';
import type { WellTypeIcon } from 'src/shared/constants/well-type-icon';
import { hasValue } from 'src/shared/utils/common';
import { formatValueWithUnit } from 'src/shared/utils/format-value-with-unit';
import { getConcatedName } from 'src/shared/utils/get-concated-name';
import { getNumberWithNumberOfDecimalPlaces } from 'src/shared/utils/get-number-with-number-of-decimal-places';
import { getWellColor } from 'src/shared/utils/get-well-color';
import { IndexField } from 'src/shared/utils/index-field';
import { isObjectWithKeys } from 'src/shared/utils/is-object-with-keys';
import { isStringNumberOrBoolean } from 'src/shared/utils/is-string-number-or-boolean';
import { LoadingField } from 'src/shared/utils/loading-field';
import { parsePrimitivesFromString } from 'src/shared/utils/parse-primitives-from-string';
import { addGOplanPrefix } from 'src/shared/utils/prefixes';
import type { AppSettingsStore } from 'src/store/app-settings/app-settings-store';
import type { Directories } from 'src/store/directories/directories.store';
import type { TRefQuery } from 'src/store/directories/types';
import type { SystemSettings } from 'src/store/system-settings';
import type { SystemSettingsService } from 'src/store/system-settings/system-settings.store';

import { ShadowPadRigOperation } from '../../features/data-items-full/presenter/shadow-pad-rig-operation';
import { ShadowWellRigOperation } from '../../features/data-items-full/presenter/shadow-well-rig-operation';
import { rseSettingsFieldNames } from '../../features/rse-readiness-icons/rse-readiness-icons';
import { DateHelper } from '../../shared/date-helper';
import { ColorAttribute } from '../../shared/entities/color-attribute';
import { IconAttribute } from '../../shared/entities/icon-attribute';
import { RegularAttribute } from '../../shared/entities/regular-attribute';
import { TitleAttribute } from '../../shared/entities/title-attribute';
import { isValueStringOrNumber } from '../../shared/is-valid-text-value';
import { ConflictRigOperation } from '../conflict-sidebar/entities/conflict-rig-operation';
import { SidebarPadRigOperation, SidebarWellRigOperation } from '../pads-and-wells-sidebar/entities';
import type { RigOperationsDnd } from '../rig-operations-dnd/rig-operations-dnd-module';

import {
  ChartRig,
  PadRigOperation,
  RigsGroup,
  TemporaryChartRig,
  TemporaryPadRigOperation,
  TemporaryWellRigOperation,
  WellRigOperation,
} from './entities';
import PAD_TOOLTIP_VIEW from './pad-tooltip-view.json';
import type { RigsChartView } from './rigs-chart-view';

const TEMP_DEFAULT_COLOR = 'transparent';

export class RigsViewSettingsProvider {
  private readonly chartView: RigsChartView;
  private readonly directories: Directories;
  private readonly appSettings: AppSettingsStore;
  private readonly systemSettingsService: SystemSettingsService;

  @observable private isLoading = false;
  @observable private colorSettings: TPaletteSettingsData | null = null;

  constructor(
    chartView: RigsChartView,
    directories: Directories,
    appSettings: AppSettingsStore,
    systemSettingsService: SystemSettings,
  ) {
    this.chartView = chartView;
    this.directories = directories;
    this.appSettings = appSettings;
    this.systemSettingsService = systemSettingsService;
    makeObservable(this);
  }

  private receiveWellAttributeValue(
    item: RigsViewSettingsProvider.ItemWithFieldNameGetter,
    settingsItemView: Omit<ViewSettings.SettingsControlView, 'control' | 'fieldId'> & {
      control?: string;
      fieldId?: string;
    },
  ): string | null {
    const {
      attrName,
      fieldId,
      refObjectAttr,
      refObjectType,
      refQuery,
      numberOfDecimalPlaces = 2,
      attrConcat,
      type,
      delimiter,
    } = settingsItemView;

    if (fieldId) {
      switch (fieldId) {
        case 'coreSamplingInterval': {
          const value = item.getFieldValue('GOplan_GeologicalTask.coreSamplingIntervalId');

          const directory = this.directories.getObject('Common_CoreSamplingInterval');

          if (!value || !Array.isArray(value) || !directory) {
            return null;
          }

          const directoryMap = new Map(directory.map((dv) => [dv.id, dv]));

          const labels = [];

          for (const intervalId of value) {
            const label = directoryMap.get(intervalId)?.data['name'];

            if (!label || typeof label !== 'string') {
              continue;
            }

            labels.push(label);
          }

          return labels.length ? labels.join(', ') : null;
        }
        case 'extractionVolume': {
          const value = item.getFieldValue('GOplan_GeologicalTask.extractionVolume');

          return value?.toString() ?? null;
        }
        case 'reservoir': {
          const value = item.getFieldValue('GOplan_GeologicalTask.reservoirIds');

          const directory = this.directories.getObject('Common_Reservoir');

          if (!value || !Array.isArray(value) || !directory) {
            return null;
          }

          const directoryMap = new Map(directory.map((dv) => [dv.id, dv]));

          const labels = [];

          for (const reservoirId of value) {
            const label = directoryMap.get(reservoirId)?.data['name'];

            if (!label || typeof label !== 'string') {
              continue;
            }

            labels.push(label);
          }

          return labels.length ? labels.join(', ') : null;
        }
      }
    }

    const attrValue = ((): unknown => {
      if (attrName) {
        const itemValue = item.getFieldValue(attrName);
        const stringifiedValue = itemValue?.toString();

        if (hasValue(stringifiedValue) && !Number.isNaN(Number(itemValue)) && stringifiedValue.includes('.')) {
          return getNumberWithNumberOfDecimalPlaces(stringifiedValue, false, numberOfDecimalPlaces);
        } else {
          return itemValue;
        }
      }

      return null;
    })();

    if (attrName && refQuery && refObjectAttr && refObjectType) {
      const refObject = this.directories.getJoinedObject(refQuery);

      const attributeObject = refObject?.find((refObjectItem) => {
        const idFieldValue = refObjectItem[refQuery.objectType]?.id;

        return idFieldValue?.toString() === attrValue?.toString();
      });

      const attribute = attributeObject?.[refObjectType]?.data?.[refObjectAttr];

      return hasValue(attribute) ? attribute.toString() : '-';
    } else if (refObjectAttr && refObjectType) {
      const refObject = this.directories.getObject(refObjectType);
      const attributeObject = refObject?.find(({ id }) => id === attrValue);
      const attribute = attributeObject?.data[refObjectAttr];

      return hasValue(attribute) ? attribute.toString() : null;
    } else if (attrConcat) {
      return attrConcat
        .reduce<string[]>((result, attr) => {
          const value = item.getFieldValue(attr);
          const validatedValue = (() => {
            if (type !== 'Date' || (typeof value !== 'number' && typeof value !== 'string')) {
              return null;
            }

            if (typeof value === 'number') {
              return DateHelper.unixToDateFormat(value);
            }

            if (Number.isNaN(Number(value))) {
              return DateHelper.unixToDateFormat(value);
            }

            return DateHelper.unixToDateFormat(Number(value));
          })();

          if (validatedValue) {
            result.push(validatedValue);
          }

          return result;
        }, [])
        .join(delimiter);
    } else {
      if (settingsItemView.type === 'Date') {
        const validatedValue = (() => {
          if (type !== 'Date' || (typeof attrValue !== 'number' && typeof attrValue !== 'string')) {
            return null;
          }

          if (typeof attrValue === 'number') {
            return DateHelper.unixToDateFormat(attrValue);
          }

          if (Number.isNaN(Number(attrValue))) {
            return DateHelper.unixToDateFormat(attrValue);
          }

          return DateHelper.unixToDateFormat(Number(attrValue));
        })();

        return validatedValue;
      }

      if (attrValue !== 'null' && isStringNumberOrBoolean(attrValue)) {
        return attrValue.toString();
      } else {
        return null;
      }
    }
  }

  private receivePadAttributeValue(
    item: PadRigOperation | SidebarPadRigOperation,
    view: ViewSettings.SettingsControlView,
  ): string {
    const { attrName, fieldId, refObjectAttr, refObjectType, refQuery } = view;

    const attrValue = item.id;

    if (attrName && refQuery && refObjectAttr) {
      const refObject = this.directories.getJoinedObjectDeprecated(refQuery);

      const attributeObject = refObject?.find((refObjectItem) => {
        const idFieldValue = refObjectItem[attrName];

        return idFieldValue.toString() === attrValue.toString();
      });

      const attributeName = `${refObjectType}.${refObjectAttr}`;
      const attribute = attributeObject?.[attributeName];

      return attribute ? attribute.toString() : '-';
    } else if (refObjectAttr && refObjectType) {
      const refObject = this.directories.getObject(refObjectType);
      const attributeObject = refObject?.find(({ id }) => id === attrValue);
      const attribute = attributeObject?.data[refObjectAttr];

      return attribute ? attribute.toString() : '-';
    } else if (hasValue(fieldId)) {
      const value = item.getFieldValue(fieldId);

      if (!hasValue(value)) {
        return '-';
      }

      if (fieldId === 'rseWellCount') {
        return i18n.t('common:WellsCount.wellCount', { count: Number(value) });
      }

      return value.toString() ?? '-';
    } else {
      return attrValue ? attrValue.toString() : '-';
    }
  }

  private receiveRigAttributeValue(
    item: ChartRig | PadRigOperation,
    settingsItem: ViewSettings.SettingsControl,
  ): string | IndexField {
    if (settingsItem.view.fieldId === 'index') {
      return new IndexField();
    }

    const { attrName, fieldId, refObjectAttr, refObjectType, refQuery } = settingsItem.view;

    const attrValue = attrName ? item.getFieldValue(attrName) : null;

    if (attrName && refQuery && refObjectAttr) {
      const refObject = this.directories.getJoinedObjectDeprecated(refQuery);

      const attributeObject = refObject?.find((refObjectItem) => {
        const idFieldValue = refObjectItem[attrName];

        return idFieldValue?.toString() === attrValue;
      });

      const attributeName = `${refObjectType}.${refObjectAttr}`;
      const attribute = attributeObject?.[attributeName];

      const valueInterpretation = this.directories.getValueInterpretation(fieldId, attribute, true);

      if (hasValue(valueInterpretation)) {
        return valueInterpretation;
      }

      return attribute ? attribute.toString() : '-';
    } else if (refObjectAttr && refObjectType) {
      const refObject = this.directories.getObject(refObjectType);
      const attributeObject = refObject?.find(({ id }) => id.toString() === attrValue);
      const attribute = attributeObject?.data[refObjectAttr];

      const valueInterpretation = this.directories.getValueInterpretation(fieldId, attribute, true);

      if (hasValue(valueInterpretation)) {
        return valueInterpretation;
      }

      return attribute ? attribute.toString() : '-';
    } else {
      const valueInterpretation = this.directories.getValueInterpretation(fieldId, attrValue, true);

      if (hasValue(valueInterpretation)) {
        return valueInterpretation;
      }

      return attrValue ? attrValue.toString() : '-';
    }
  }

  private receiveWellTitle(item: WellRigOperation | ShadowWellRigOperation | TemporaryWellRigOperation): string | null {
    const { attrName, refObjectAttr, refObjectType } = this.chartView.view.carpet.wells;

    const refObject = this.directories.getObject(refObjectType);
    const labelObject = refObject?.find(({ id }) => id === item.getFieldValue(attrName));
    const label = labelObject?.data[refObjectAttr];

    return label ? label.toString() : null;
  }

  private receiveGroupTitle(item: RigsGroup): string | null {
    const label = item.getFieldValue('name');

    return isValueStringOrNumber(label) ? label.toString() : null;
  }

  @flow.bound
  private async *loadCarpetDictionaries(view: RigsChartViewTypes.RigsChartView) {
    this.isLoading = true;

    const { refObjectType, pads, wells } = view.carpet;
    const refObjectTypes = [
      refObjectType,
      wells.refObjectType,
      ...pads.headers.reduce((objects: string[], { refObjectType }) => {
        if (refObjectType) {
          objects.push(refObjectType);
        }

        return objects;
      }, []),
    ];

    const refQueries: TRefQuery[] = [];

    wells.tooltip.defaultAttributes.forEach((attributeList) => {
      attributeList.forEach((attribute) => {
        if (attribute.type === 'Formula') {
          attribute.arguments.forEach(({ refObjectType, refQuery }) => {
            if (refObjectType) {
              refObjectTypes.push(refObjectType);
            }

            if (refQuery) {
              refQueries.push(refQuery);
            }
          });
        } else {
          if (attribute.refObjectType) {
            refObjectTypes.push(attribute.refObjectType);
          }
        }
      });
    }, []);

    await this.directories.loadObjects(refObjectTypes);
    await this.directories.loadJoinedObjects(refQueries);
    yield;

    this.isLoading = false;
  }

  @flow.bound
  private async *loadColorSettings() {
    const settings = await this.systemSettingsService.loadSettings<TPaletteSettingsData>('palette');
    yield;

    this.colorSettings = settings;
  }

  @flow.bound
  private async *loadDictionaries(
    shownWellAttributesFields: Record<string, ViewSettings.SettingsControl[] | undefined>,
  ) {
    this.isLoading = true;

    const refObjects: string[] = [];
    const joinedObjects: TRefQuery[] = [];

    for (const shownAttributes of Object.values(shownWellAttributesFields)) {
      if (!shownAttributes) {
        continue;
      }

      for (const shownWellAttributesField of shownAttributes) {
        const { view } = shownWellAttributesField;
        const { refObjectType, refQuery } = view;

        if (refQuery) {
          joinedObjects.push(refQuery);
        } else if (refObjectType) {
          refObjects.push(refObjectType);
        }
      }
    }

    await Promise.all([
      this.directories.loadObjects([...refObjects, 'Common_PassageType', 'Common_Block']),
      this.directories.loadJoinedObjectsDeprecated(joinedObjects),
    ]);
    yield;

    this.isLoading = false;
  }

  @action.bound
  init(): VoidFunction {
    this.loadCarpetDictionaries(this.chartView.view);
    this.loadDictionaries(this.shownAttributesFields);
    this.loadColorSettings();

    const disposeCarpetDictionariesLoading = reaction(
      () => this.chartView.view,
      (view) => {
        this.loadCarpetDictionaries(view);
      },
    );

    const disposeWellDictionariesLoading = reaction(
      () => this.shownAttributesFields,
      (shownAttributesFields) => {
        this.loadDictionaries(shownAttributesFields);
      },
    );

    return () => {
      disposeCarpetDictionariesLoading();
      disposeWellDictionariesLoading();
    };
  }

  @computed
  get shownAttributesFields(): Record<string, ViewSettings.SettingsControl[] | undefined> {
    const attributes: Record<string, ViewSettings.SettingsControl[]> = {};

    function findSettingViewInGroups(
      settingFieldId: string,
      viewSettingGroups: ViewSettings.RawSettingsGroup[],
    ): ViewSettings.SettingsControlView | undefined {
      for (const group of viewSettingGroups) {
        const viewSetting = group.items.find((viewSetting) => viewSetting.fieldId === settingFieldId);

        if (viewSetting) {
          return viewSetting;
        }
      }
    }

    for (const [settingKey, settingValues] of Object.entries(this.chartView.infoSettingsValues)) {
      const settingAttributes: ViewSettings.SettingsControl[] = [];

      if (!isObjectWithKeys(settingValues)) {
        continue;
      }

      const viewSettings = this.chartView.settings[settingKey];

      if (settingValues.type === ViewSettings.ViewSettingsTabsDataTypes.flat) {
        for (const settingValue of settingValues.settings) {
          if (!settingValue.isShown) {
            continue;
          }

          if (viewSettings.type !== settingValues.type) {
            continue;
          }

          const settingView = viewSettings.items.find((viewSetting) => viewSetting.fieldId === settingValue.fieldId);

          if (settingView) {
            settingAttributes.push({ view: settingView, values: settingValue });
          }
        }
      } else {
        for (const settingsGroup of settingValues.settings) {
          for (const settingValue of settingsGroup.items) {
            if (!settingValue.isShown) {
              continue;
            }

            if (viewSettings.type !== settingValues.type) {
              continue;
            }

            const settingView = findSettingViewInGroups(settingValue.fieldId, viewSettings.groups);

            if (settingView) {
              settingAttributes.push({ view: settingView, values: settingValue });
            }
          }
        }
      }

      attributes[viewSettings.fieldId] = settingAttributes;
    }

    return attributes;
  }

  getAttributes = computedFn(
    (
      item:
        | WellRigOperation
        | ChartRig
        | PadRigOperation
        | SidebarPadRigOperation
        | SidebarWellRigOperation
        | ShadowWellRigOperation
        | TemporaryPadRigOperation
        | TemporaryWellRigOperation,
    ): (string | IndexField)[] | null => {
      if (
        item instanceof WellRigOperation ||
        item instanceof SidebarWellRigOperation ||
        item instanceof ShadowWellRigOperation ||
        item instanceof TemporaryWellRigOperation
      ) {
        return (
          this.shownAttributesFields['wellSettings']?.map(
            (shownAttributesField) => this.receiveWellAttributeValue(item, shownAttributesField.view) ?? '-',
          ) ?? null
        );
      }
      if (item instanceof ChartRig) {
        return (
          this.shownAttributesFields['rigSettings']?.map((shownAttributesFields) =>
            this.receiveRigAttributeValue(item, shownAttributesFields),
          ) ?? null
        );
      }
      if (item instanceof PadRigOperation || item instanceof SidebarPadRigOperation) {
        return (
          this.shownAttributesFields['padSettings']
            ?.filter((settingsItem) => !rseSettingsFieldNames.includes(settingsItem.values.fieldId))
            .map((shownAttributesFields) => this.receivePadAttributeValue(item, shownAttributesFields.view)) ?? null
        );
      }

      return null;
    },
  );

  private getChartRigTitle(item: ChartRig | TemporaryChartRig): string {
    const titleView = this.chartView.view.carpet.rows;

    const rigItem = item instanceof TemporaryChartRig ? item.commonRigData : item.item;

    return getConcatedName(this.directories, titleView, rigItem) || '';
  }

  getTitle(
    item:
      | RigOperationsDnd.ViewItem
      | SidebarWellRigOperation
      | SidebarPadRigOperation
      | TemporaryChartRig
      | ShadowPadRigOperation
      | ShadowWellRigOperation
      | TemporaryWellRigOperation,
  ): string | LoadingField {
    if (this.isLoading) {
      return new LoadingField();
    }

    if (item instanceof RigsGroup) {
      const fieldValue = this.receiveGroupTitle(item);

      if (isValueStringOrNumber(fieldValue)) {
        return fieldValue.toString();
      }

      return '';
    }

    if (item instanceof TemporaryChartRig) {
      return this.getChartRigTitle(item);
    }

    if (item instanceof ChartRig) {
      return this.getChartRigTitle(item);
    }

    if (item instanceof PadRigOperation || item instanceof TemporaryPadRigOperation) {
      const padHeaders = this.chartView.view.carpet.pads.headers.filter(({ control }) => control === Controls.title);
      const padHeadersValues: string[] = [];

      for (const header of padHeaders) {
        if (header.refObjectAttr && header.refObjectType === 'Common_Pad') {
          const headerValue = item.getFieldValue(header.refObjectAttr);

          if (hasValue(headerValue)) {
            padHeadersValues.push(headerValue.toString());
          }
        }
      }

      return padHeadersValues.join(', ');
    }

    if (item instanceof WellRigOperation || item instanceof TemporaryWellRigOperation) {
      return this.receiveWellTitle(item) || '';
    }

    if (item instanceof SidebarWellRigOperation) {
      return item.title;
    }

    if (item instanceof SidebarPadRigOperation) {
      return item.title;
    }

    if (item instanceof ShadowPadRigOperation) {
      return item.pad.title;
    }

    if (item instanceof ShadowWellRigOperation) {
      return this.receiveWellTitle(item) || '';
    }

    if (item instanceof ConflictRigOperation) {
      return 'Conflict Well';
    }

    return '';
  }

  getDescription(item: ChartRig | TemporaryChartRig): (string | IndexField)[] {
    if (item instanceof TemporaryChartRig) {
      return ['-'];
    }

    return (
      this.shownAttributesFields['rigSettings']?.map((shownRigAttributesFields) =>
        this.receiveRigAttributeValue(item, shownRigAttributesFields),
      ) ?? []
    );
  }

  getPadTooltipAttributes(item: PadRigOperation): RegularAttribute[] | LoadingField | null {
    if (this.isLoading) {
      return new LoadingField();
    }

    const visibilitySettings = this.shownAttributesFields['padSettings'];

    if (!visibilitySettings) {
      return null;
    }

    const tooltipAttributes =
      this.chartView.view.carpet.pads.tooltip?.additionalAttributes.filter((a) =>
        visibilitySettings.find((s) => s.values.fieldId === a.fieldId),
      ) ?? [];

    const parsedAdditionalAttributes: RegularAttribute[] = [];

    tooltipAttributes?.forEach((rawTooltipAttr) => {
      const attribute = this.processTooltipAttribute(item, rawTooltipAttr);
      if (attribute && attribute instanceof RegularAttribute) {
        parsedAdditionalAttributes.push(attribute);
      }
    });

    return parsedAdditionalAttributes;
  }

  getPadTooltipDate(item: PadRigOperation): string {
    const startDate = DateHelper.unixToDateFormat(item.x.start);
    const endDate = DateHelper.unixToDateFormat(item.x.end);

    return `${startDate} - ${endDate}`;
  }

  private processTooltipAttribute(
    item: WellRigOperation | SidebarWellRigOperation | PadRigOperation,
    view: ViewSettings.SettingsControlView,
  ): ColorAttribute | IconAttribute<WellTypeIcon> | RegularAttribute | null {
    const label = view.fieldId ? this.directories.getFieldLabel(view.fieldId) : null;
    const unit = view.fieldId ? this.directories.getFieldUnit(view.fieldId) : null;

    if (!hasValue(label)) {
      return null;
    }

    if (item instanceof WellRigOperation || item instanceof SidebarWellRigOperation) {
      const value = this.receiveWellAttributeValue(item, view) ?? '-';

      const valueWithUnit = formatValueWithUnit(value, unit);

      return new RegularAttribute(label, valueWithUnit);
    }

    if (item instanceof PadRigOperation) {
      const value = this.receivePadAttributeValue(item, view);

      const valueWithUnit = formatValueWithUnit(value, unit);

      return new RegularAttribute(label, valueWithUnit);
    }

    return null;
  }

  private getDefaultWellTooltipAttribute(
    well: WellRigOperation | SidebarWellRigOperation,
    defaultAttr: RigsChartViewTypes.DefaultWellTooltipAttribute,
  ): RigsViewSettingsProvider.WellTooltipAttribute | null {
    switch (defaultAttr.type) {
      case 'Color': {
        const wellPurposeValue = this.receiveWellAttributeValue(well, defaultAttr) ?? '-';

        if (!hasValue(wellPurposeValue)) {
          return null;
        }

        const wellTypeColor = this.getWellColorDep(well.data);

        return new ColorAttribute(wellTypeColor, this.isLoading ? new LoadingField() : wellPurposeValue);
      }

      case 'Icon': {
        const wellTypeIconName = this.getWellIcon(well.data);

        if (!hasValue(wellTypeIconName)) {
          return null;
        }

        const wellTypeValue = this.receiveWellAttributeValue(well, defaultAttr) ?? '-';

        return new IconAttribute<WellTypeIcon>(
          wellTypeIconName,
          this.isLoading ? new LoadingField() : (wellTypeValue ?? ''),
        );
      }

      case 'Title': {
        const title = this.receiveWellAttributeValue(well, defaultAttr) ?? '-';

        return new TitleAttribute(this.isLoading ? new LoadingField() : title);
      }

      case 'Date': {
        const { fieldId } = defaultAttr;
        const label = this.directories.getFieldLabel(fieldId) ?? '';
        const attrValue = this.receiveWellAttributeValue(well, defaultAttr) ?? '-';

        return new RegularAttribute(label, attrValue);
      }

      case 'Formula': {
        const { body, resultType, arguments: args, fieldId } = defaultAttr;

        const label = this.directories.getFieldLabel(fieldId) ?? '';
        const nullValue = this.directories.getFieldNullValue(fieldId) ?? '-';
        const controls = args.map(({ control }) => control);

        const formulaFunction = new Function(...controls, body);

        const argValues = args.map((arg) => {
          return this.receiveWellAttributeValue(well, arg);
        });

        try {
          const value = formulaFunction(...argValues);
          const validatedValue =
            hasValue(value) && (typeof value === 'string' || typeof value === 'number') && resultType === 'Date'
              ? DateHelper.unixToDateFormat(value)
              : (value ?? null);

          return new RegularAttribute(label, validatedValue ?? nullValue);
        } catch (e) {
          console.error(e);

          return new RegularAttribute(label, nullValue);
        }
      }

      default:
        return null;
    }
  }

  getWellTooltip(well: WellRigOperation | SidebarWellRigOperation): RigsViewSettingsProvider.WellTooltip | undefined {
    try {
      const rawAttributes = this.shownAttributesFields['wellSettings'];
      const parsedAttributesLists: RigsViewSettingsProvider.WellTooltipAttribute[][] = [];

      this.chartView.view.carpet.wells.tooltip?.defaultAttributes.forEach((attrList) => {
        const parsedAttributes: RigsViewSettingsProvider.WellTooltipAttribute[] = [];

        attrList.forEach((rawDefaultAttr) => {
          const defaultAttr = this.getDefaultWellTooltipAttribute(well, rawDefaultAttr);

          if (defaultAttr) {
            parsedAttributes.push(defaultAttr);
          }
        });

        parsedAttributesLists.push(parsedAttributes);
      });

      const lastAttributesList = parsedAttributesLists.at(-1);

      rawAttributes?.forEach((rawTooltipAttr) => {
        const attribute = this.processTooltipAttribute(well, rawTooltipAttr.view);

        if (attribute) {
          lastAttributesList?.push(attribute);
        }
      });

      return {
        attributes: parsedAttributesLists,
      };
    } catch (e) {
      console.error(e);
    }
  }

  getPadAttributes(
    item: PadRigOperation | SidebarPadRigOperation | ShadowPadRigOperation | TemporaryPadRigOperation,
  ): PadAttributeIcon[] | null {
    if (this.isLoading) {
      return null;
    }

    const view: PadTooltipView.Item[] = PAD_TOOLTIP_VIEW;

    return view.reduce<PadAttributeIcon[]>((attributeNames, attributesGroup) => {
      const hasViewPadAttributes = attributesGroup.attributes.some((attribute) =>
        attribute.values.some((value) => {
          const fieldName = value.value;

          return item.getFieldValue(fieldName);
        }),
      );

      if (hasViewPadAttributes && isPadAttributeIcon(attributesGroup.icon)) {
        attributeNames.push(attributesGroup.icon);
      }

      return attributeNames;
    }, []);
  }

  getPadGradient(item: PadRigOperation): string | null {
    const chosenColorSettings = this.chosenColorSettings;

    if (!chosenColorSettings) {
      return null;
    }

    const { gradientColors } = item.wellRigOperations.reduce(
      (acc: { gradientColors: string[]; totalPercentage: number }, wellRigOperation, index) => {
        const color = this.getWellColor(wellRigOperation);
        const percentage = (() => {
          if (!wellRigOperation.x) {
            return null;
          }

          if (index === item.wellRigOperations.length - 1) {
            return 100 - acc.totalPercentage;
          }

          const wellDuration = wellRigOperation.x.end - wellRigOperation.x.start;
          const padDuration = item.x.end - item.x.start;

          return Math.trunc((wellDuration / padDuration) * 100);
        })();

        if (!hasValue(percentage)) {
          return acc;
        }

        acc.gradientColors.push(`${color} ${acc.totalPercentage}% ${acc.totalPercentage + percentage}%`);
        acc.totalPercentage = acc.totalPercentage + percentage;

        return acc;
      },
      { gradientColors: [], totalPercentage: 0 },
    );

    if (!gradientColors.length) {
      return null;
    }

    return `linear-gradient(to right, ${gradientColors.join(', ')})`;
  }

  @computed
  private get chosenColorSettings(): ViewSettings.SettingsControlValue | null {
    const chartColorSettings = (
      this.chartView.infoSettingsValues['colorTab'].settings as ViewSettings.SettingsGroupValues[]
    ).reduce((acc: ViewSettings.SettingsControlValue[], group) => [...acc, ...group.items], []);

    return chartColorSettings.find((setting) => setting.isShown) ?? null;
  }

  getWellColor(
    item: WellRigOperation | SidebarWellRigOperation | TemporaryWellRigOperation | ShadowWellRigOperation,
  ): string {
    if (!this.chosenColorSettings) {
      console.error('No color settings chosen');
      return TEMP_DEFAULT_COLOR;
    }

    const parentPad = (() => {
      if (
        item instanceof WellRigOperation ||
        item instanceof SidebarWellRigOperation ||
        item instanceof ShadowWellRigOperation
      ) {
        return item.parentPad;
      }

      return null;
    })();

    const parentPadData = (() => {
      if (item instanceof WellRigOperation) {
        return item.parentPad?.item?.data;
      }

      if (item instanceof SidebarWellRigOperation) {
        return item.parentPad?.data;
      }

      if (item instanceof ShadowWellRigOperation) {
        return item.parentPad?.pad?.data;
      }

      return null;
    })() as Record<string, string | number | boolean>;

    switch (this.chosenColorSettings.fieldId) {
      case 'cluster': {
        if (!parentPadData) {
          console.error('WellRigOperation has no parentPad');
          return TEMP_DEFAULT_COLOR;
        }

        const blockToField = this.directories.getObject('Common_BlockFieldLink') ?? [];

        const blockToFieldItem = blockToField.find((item) => item.data['blockId'] === parentPadData['blockId']);

        if (!blockToFieldItem) {
          return TEMP_DEFAULT_COLOR;
        }

        const clusterId = blockToFieldItem.data['clusterId'];

        if (!hasValue(clusterId)) {
          return TEMP_DEFAULT_COLOR;
        }

        return this.colorSettings?.data['Common_Cluster']?.[clusterId as number] ?? TEMP_DEFAULT_COLOR;
      }
      case 'blockId': {
        if (!parentPadData) {
          console.error('WellRigOperation has no parentPad');
          return TEMP_DEFAULT_COLOR;
        }

        const blockId = parentPadData['blockId'];

        if (!hasValue(blockId)) {
          return TEMP_DEFAULT_COLOR;
        }

        return this.colorSettings?.data['Common_Block']?.[blockId as number] ?? TEMP_DEFAULT_COLOR;
      }

      case 'fieldId': {
        if (!parentPadData) {
          console.error('WellRigOperation has no parentPad');
          return TEMP_DEFAULT_COLOR;
        }

        const fieldId = parentPadData['fieldId'];

        if (!hasValue(fieldId)) {
          return TEMP_DEFAULT_COLOR;
        }

        return this.colorSettings?.data['Common_Field']?.[fieldId as number] ?? TEMP_DEFAULT_COLOR;
      }

      case 'subsoilUser': {
        if (!parentPadData) {
          console.error('WellRigOperation has no parentPadData');
          return TEMP_DEFAULT_COLOR;
        }

        const block = this.directories
          .getObject('Common_Block')
          ?.find((block) => block.id === parentPadData['blockId']);

        if (!block) {
          return TEMP_DEFAULT_COLOR;
        }

        const drillingContractorId = parsePrimitivesFromString(block.data['organizationId']);

        if (!hasValue(drillingContractorId)) {
          return TEMP_DEFAULT_COLOR;
        }

        return this.colorSettings?.data['Common_Organization']?.[drillingContractorId as number] ?? TEMP_DEFAULT_COLOR;
      }

      case 'wellPurposeId': {
        const wellPurposeId = item.data['GOplan_GeologicalTask.wellPurposeId'];

        if (!hasValue(wellPurposeId)) {
          return TEMP_DEFAULT_COLOR;
        }

        return this.colorSettings?.data['Common_WellPurpose']?.[wellPurposeId as number] ?? TEMP_DEFAULT_COLOR;
      }

      case 'fluidTypeId': {
        const fluidTypeId = item.data['GOplan_GeologicalTask.fluidTypeId'];

        if (!hasValue(fluidTypeId)) {
          return TEMP_DEFAULT_COLOR;
        }

        return this.colorSettings?.data['Common_FluidType']?.[fluidTypeId as number] ?? TEMP_DEFAULT_COLOR;
      }

      case 'padReadiness': {
        if (!parentPad) {
          console.error('WellRigOperation has no parentPad');
          return TEMP_DEFAULT_COLOR;
        }

        const { padReady } = parentPad.rseReadiness.padReadiness;

        switch (padReady) {
          case 'ready': {
            return 'var(--green-color)';
          }
          case 'inProcess': {
            return 'var(--orange-color)';
          }
          case 'notReady': {
            return 'var(--bright-red)';
          }
          default: {
            return TEMP_DEFAULT_COLOR;
          }
        }
      }

      case 'padDocumentationReadiness': {
        if (!parentPad) {
          console.error('WellRigOperation has no parentPad');
          return TEMP_DEFAULT_COLOR;
        }

        const { landReady } = parentPad.rseReadiness.padReadiness;

        switch (landReady) {
          case 'ready': {
            return 'var(--green-color)';
          }
          case 'inProcess': {
            return 'var(--orange-color)';
          }
          case 'notReady': {
            return 'var(--bright-red)';
          }
          default: {
            return TEMP_DEFAULT_COLOR;
          }
        }
      }

      case 'routeReadiness': {
        if (!parentPad) {
          console.error('WellRigOperation has no parentPad');
          return TEMP_DEFAULT_COLOR;
        }

        const { routeReady } = parentPad.rseReadiness.padReadiness;

        switch (routeReady) {
          case 'ready': {
            return 'var(--green-color)';
          }
          case 'inProcess': {
            return 'var(--orange-color)';
          }
          case 'notReady': {
            return 'var(--bright-red)';
          }
          default: {
            return TEMP_DEFAULT_COLOR;
          }
        }
      }
      case 'routeDocumentationReadiness': {
        if (!parentPad) {
          console.error('WellRigOperation has no parentPad');
          return TEMP_DEFAULT_COLOR;
        }

        const { routeDocumentationReady } = parentPad.rseReadiness.padReadiness;

        switch (routeDocumentationReady) {
          case 'ready': {
            return 'var(--green-color)';
          }
          case 'inProcess': {
            return 'var(--orange-color)';
          }
          case 'notReady': {
            return 'var(--bright-red)';
          }
          default: {
            return TEMP_DEFAULT_COLOR;
          }
        }
      }

      case 'electricityReadiness': {
        if (!parentPad) {
          console.error('WellRigOperation has no parentPad');
          return TEMP_DEFAULT_COLOR;
        }

        const { electricityReady } = parentPad.rseReadiness.padReadiness;

        switch (electricityReady) {
          case 'ready': {
            return 'var(--green-color)';
          }
          case 'inProcess': {
            return 'var(--orange-color)';
          }
          case 'notReady': {
            return 'var(--bright-red)';
          }
          default: {
            return TEMP_DEFAULT_COLOR;
          }
        }
      }

      case 'routeType': {
        const routeTypeId = item.data['routeType'];

        const routeTypeObj = this.directories
          .getObject('Common_PassageType')
          ?.find((routeType) => routeType.id === routeTypeId);

        if (!routeTypeObj) {
          return TEMP_DEFAULT_COLOR;
        }

        switch (routeTypeObj.data['slug']) {
          case 'winter': {
            return '#3954D0';
          }

          case 'year-round': {
            return 'green';
          }
          default: {
            return TEMP_DEFAULT_COLOR;
          }
        }
      }

      default: {
        return TEMP_DEFAULT_COLOR;
      }
    }
  }

  private getWellColorDep(item: Record<string, unknown>): string {
    return getWellColor(item, this.appSettings.wellColorRules);
  }

  getWellIcon(item: Record<string, unknown>): WellTypeIcon | undefined {
    return this.appSettings.getWellIcon(item, addGOplanPrefix('GeologicalTask.wellTypeId'));
  }

  /** Get start of first stage in rig operation. */
  getRigOperationStartUnix(item: WellRigOperation): number | null {
    try {
      const { attrName } = this.chartView.view.carpet.wells.rigOperationStart;

      const startDate = item.getFieldValue(attrName);

      if (startDate) {
        return Number.parseInt(startDate.toString()) ?? null;
      }
    } catch (e) {
      console.error(e);
    }

    return null;
  }
}

export namespace RigsViewSettingsProvider {
  export interface ItemWithFieldNameGetter {
    getFieldValue(fieldName: string): unknown;
  }

  export type WellTooltip = {
    attributes: WellTooltipAttribute[][];
  };

  export type WellTooltipAttribute = ColorAttribute | IconAttribute<WellTypeIcon> | RegularAttribute | TitleAttribute;

  export type PadTooltipAttributeValue = {
    value: string;
    rawTitle?: string;
  };

  export type PadTooltipAttribute = {
    rawTitle: string;
    values: PadTooltipAttributeValue[];
    icon: PadAttributeIcon | null;
  };
}

export namespace PadTooltipView {
  export type AttributeValue = {
    value: string;
    type: string;
  };

  export type Attribute = {
    values: AttributeValue[];
    title?: string;
    delimiter?: string;
  };

  export type Item = {
    title: string;
    attributes: Attribute[];
    icon: string;
  };
}
